import React from 'react';
import { ArrowRight, ArrowLeft, ArrowDown } from 'lucide-react';

/**
 * Компонент ввода параметров прямоугольной формы
 */
const RectangleSection = ({ formData, handleInputChange, errors }) => {
  return (
    <div className="mb-6 bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg border border-cyan-100 dark:border-cyan-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
        Размеры прямоугольной конструкции
      </h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Длина */}
        <div>
          <label htmlFor="length" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <ArrowRight className="w-4 h-4 mr-1 text-cyan-600" />
            Длина (м)
          </label>
          <input
            type="text"
            id="length"
            name="length"
            value={formData.length}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.length ? 'input-error' : ''}`}
            placeholder="Введите длину"
          />
          {errors.length && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.length}
            </p>
          )}
        </div>

        {/* Ширина */}
        <div>
          <label htmlFor="width" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <ArrowLeft className="w-4 h-4 mr-1 text-cyan-600" />
            Ширина (м)
          </label>
          <input
            type="text"
            id="width"
            name="width"
            value={formData.width}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.width ? 'input-error' : ''}`}
            placeholder="Введите ширину"
          />
          {errors.width && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.width}
            </p>
          )}
        </div>

        {/* Высота */}
        <div>
          <label htmlFor="height" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <ArrowDown className="w-4 h-4 mr-1 text-cyan-600" />
            Высота (м)
          </label>
          <input
            type="text"
            id="height"
            name="height"
            value={formData.height}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.height ? 'input-error' : ''}`}
            placeholder="Введите высоту"
          />
          {errors.height && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.height}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Визуальное представление */}
        <div className="bg-white dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
          <div className="flex justify-center items-center h-28">
            <div 
              className="relative bg-cyan-100 dark:bg-cyan-800 border-2 border-cyan-400 dark:border-cyan-600" 
              style={{ 
                width: `${Math.min(100, parseFloat(formData.length || 0) * 20)}px`, 
                height: `${Math.min(80, parseFloat(formData.width || 0) * 16)}px` 
              }}
            >
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-600 dark:text-gray-300">
                {formData.length} м
              </div>
              <div className="absolute -left-6 top-1/2 transform -translate-y-1/2 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {formData.width} м
              </div>
              <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-cyan-800 dark:text-cyan-200">
                h: {formData.height} м
              </div>
            </div>
          </div>
        </div>

        {/* Типовые размеры */}
        <div className="bg-white dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Типовые размеры:</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <button 
              type="button"
              onClick={() => {
                handleInputChange({ target: { name: 'length', value: '6' } });
                handleInputChange({ target: { name: 'width', value: '6' } });
                handleInputChange({ target: { name: 'height', value: '0.2' } });
              }}
              className="p-1 bg-gray-100 dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500 text-center hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
            >
              Фундамент 6×6×0.2 м
            </button>
            <button 
              type="button"
              onClick={() => {
                handleInputChange({ target: { name: 'length', value: '4' } });
                handleInputChange({ target: { name: 'width', value: '4' } });
                handleInputChange({ target: { name: 'height', value: '0.15' } });
              }}
              className="p-1 bg-gray-100 dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500 text-center hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
            >
              Площадка 4×4×0.15 м
            </button>
            <button 
              type="button"
              onClick={() => {
                handleInputChange({ target: { name: 'length', value: '10' } });
                handleInputChange({ target: { name: 'width', value: '0.4' } });
                handleInputChange({ target: { name: 'height', value: '0.8' } });
              }}
              className="p-1 bg-gray-100 dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500 text-center hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
            >
              Лента 10×0.4×0.8 м
            </button>
            <button 
              type="button"
              onClick={() => {
                handleInputChange({ target: { name: 'length', value: '3' } });
                handleInputChange({ target: { name: 'width', value: '0.3' } });
                handleInputChange({ target: { name: 'height', value: '3' } });
              }}
              className="p-1 bg-gray-100 dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500 text-center hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
            >
              Стена 3×0.3×3 м
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RectangleSection;